/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import basketStore from "./store/basketStore";
import { baseURL } from "../../libs/http";
import Counter from "../../components/ui/Form/FormCounter";
import DottedSeparator from "../../components/ui/DottedSeparator";
import { getDeclension } from "../../utils/tools";
import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";
import { toast, ToastContainer } from "react-toastify";
import appStore from "../../stores/appStore";
import RecipientDataModal from "./components/RecipientDataModal";
import type { RecipientFormData } from "./types/recipientModal.types";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router";

const BasketPage = () => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [orderNumbers, setOrderNumbers] = useState<string[]>([]);
  const [verificationSent, setVerificationSent] = useState(false);
  const [retryTimer, setRetryTimer] = useState(179);

  useEffect(() => {
    basketStore.updatePrices();
  }, []);

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src =
      "https://avatars.mds.yandex.net/i?id=8e294c809ff68fd77490c5cab8f9a29e_l-4276653-images-thumbs&n=13";
  };

  const handleQuantityChange = (
    refKey: string,
    warehouse: string,
    newQuantity: number
  ) => {
    basketStore.updateQuantity(refKey, warehouse, newQuantity);
  };

  const getUserAuthState = ():
    | "authenticated"
    | "authenticated_no_name"
    | "not_authenticated" => {
    if (!appStore.isLoginIn) {
      return "not_authenticated";
    }
    if (!appStore.userInfo.name) {
      return "authenticated_no_name";
    }
    return "authenticated";
  };

  const handleRequestVerification = async () => {
    setVerificationSent(true);
    setRetryTimer(179);
  };

  const navigate = useNavigate();

  const handleRecipientSubmit = async (data: RecipientFormData) => {
    try {
      const response = await basketStore.createOrder({
        username: data.firstName,
        phone: data.phone ? data.phone : appStore.userInfo.phone,
        products: basketStore.basket,
        surname: data?.lastName ? data.lastName : undefined,
      });

      if (response && response.length > 0) {
        const numbers = response.map((order: any) => order.order_number);
        setOrderNumbers(numbers);
        setIsSuccessModalOpen(true);
      }
      appStore.getUser();
      basketStore.clearBasket();
      setIsOpenModal(false);
      setVerificationSent(false);
    } catch {
      toast.error("Произошла ошибка при оформлении заказа", {
        position: "bottom-right",
        autoClose: 300,
      });
    }
  };

  // Стили для мобильных устройств
  const mobileStyles = {
    container: isMobile ? "p-2 gap-2" : "p-[30px] gap-[12px]",
    basketItem: isMobile
      ? "flex-col items-start gap-[15px] pb-[15px] p-2 border-b-[1px] border-[#C6C6C8]"
      : "flex-row justify-between items-center pb-[15px] max-h-[85px]  border-b-[1px] border-b-[#C6C6C8] last:border-b-0",
    productImage: isMobile
      ? "w-[96px] min-w-[96px] h-[70px] object-contain"
      : "w-[96px] min-w-[96px] h-[70px] object-contain",
    productInfo: isMobile ? "w-full" : "w-[630px]",
    orderSummary: isMobile
      ? "w-full p-[15px] bg-white rounded-[20px]"
      : "w-[350px] max-h-max p-[20px] bg-white rounded-[20px] shadow-[0_2px_20px_#0000001a]",
  };

  return (
    <div className={`flex flex-col ${mobileStyles.container}`}>
      <Helmet>
        <>
          <title>КЦ Авто | Корзина</title>
          <meta
            name="description"
            content={`КЦ авто корзина купить автозапчасти Корея центр`}
          />
          <meta
            name="keywords"
            content="КЦ Авто, интернет магазин автозапчастей, поиск запчастей, купить автозапчасти"
          />
        </>
      </Helmet>
      <h2
        className={
          isMobile
            ? "text-[17px] font-bold tracking-[-0.02em] text-[#232323] pl-[15px] pb-[5px]"
            : "text-[27px] font-bold tracking-[-0.02em] text-[#232323]"
        }
      >
        Корзина
      </h2>

      <div
        className={
          isMobile
            ? "flex flex-col h-[46vh] overflow-auto"
            : "flex flex-row gap-[30px]"
        }
      >
        {/* Список товаров */}
        <div
          className={`flex flex-col ${
            isMobile
              ? "gap-2"
              : "h-max gap-[15px] p-[25px] bg-white rounded-[20px] shadow-[0_2px_20px_#0000001a] w-[1000px]"
          }`}
        >
          {basketStore.basket.map((item, index) => (
            <div
              key={`${item.product.ref_key}-${item.warehouse}-${index}`}
              className={`flex ${mobileStyles.basketItem} cursor-pointer`}
            >
              <div
                className={`flex ${
                  isMobile ? "flex-row gap-[10px]" : "flex-row gap-[20px]"
                } ${mobileStyles.productInfo}`}
                onClick={() => navigate(`/product/${item.product.ref_key}`)}
              >
                <img
                  src={`${baseURL}/image/${item.product.ref_key}`}
                  onError={handleImageError}
                  alt={item.product.name}
                  className={mobileStyles.productImage}
                />
                <div className="flex flex-col">
                  <h2
                    className={
                      isMobile
                        ? "text-[13px] text-[#232323] font-semibold tracking-[-0.02em] leading-[120%] mb-[5px]"
                        : "!text-[16px] text-[#232323] font-semibold tracking-[-0.02em] leading-[100%] mb-auto"
                    }
                  >
                    {item.product.name}
                  </h2>
                  <span
                    className={
                      isMobile
                        ? "text-[11px] font-medium text-[#828282] leading-[19px] tracking-[-0.02em]"
                        : "text-[13px] font-medium text-[#828282] leading-[19px] tracking-[-0.02em]"
                    }
                  >
                    {item.warehouse}
                  </span>
                  {isMobile && (
                    <div
                      className={
                        !isMobile
                          ? "flex w-full justify-between flex-1 self-start ml-[30px]"
                          : "flex w-full justify-between flex-1 self-start mt-[15px]"
                      }
                    >
                      <Counter
                        value={item.quantity}
                        onChange={(newValue) =>
                          handleQuantityChange(
                            item.product.ref_key,
                            item.warehouse,
                            newValue
                          )
                        }
                        min={0}
                        max={
                          item.product.stocks.find(
                            (i) => i.Stock === item.warehouse
                          )?.qnt
                        }
                      />
                      <div className="flex flex-col items-end">
                        {basketStore.isLoadingPrices ? (
                          <div className="h-[20px] w-[60px] bg-gray-200 rounded-[6px] animate-pulse"></div>
                        ) : (
                          <h2
                            className={
                              isMobile
                                ? "text-[16px] font-bold tracking-[-0.02em] leading-[100%]"
                                : "text-[16px] font-semibold tracking-[-0.02em] text-[#232323] leading-[100%]"
                            }
                          >
                            ₽ {(item.product.price * item.quantity).toFixed(2)}
                          </h2>
                        )}
                        {!isMobile && (
                          <span
                            className={
                              isMobile
                                ? "text-xs text-[#828282]"
                                : "text-[#828282] tracking-[-0.02em] text-[13px] font-medium leading-[19px]"
                            }
                          >
                            {item.quantity} шт
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {!isMobile && (
                <div
                  className={
                    !isMobile
                      ? "flex w-full justify-between flex-1 self-start ml-[30px]"
                      : "flex w-full justify-between flex-1 self-start !pl-[105px]"
                  }
                >
                  <Counter
                    value={item.quantity}
                    onChange={(newValue) =>
                      handleQuantityChange(
                        item.product.ref_key,
                        item.warehouse,
                        newValue
                      )
                    }
                    min={0}
                    max={
                      item.product.stocks.find(
                        (i) => i.Stock === item.warehouse
                      )?.qnt
                    }
                  />
                  <div className="flex flex-col items-end">
                    {basketStore.isLoadingPrices ? (
                      <div className="h-[20px] w-[60px] bg-gray-200 rounded-[6px] animate-pulse"></div>
                    ) : (
                      <h2
                        className={
                          isMobile
                            ? "text-[16px] font-bold tracking-[-0.02em] leading-[100%]"
                            : "text-[16px] font-semibold tracking-[-0.02em] text-[#232323] leading-[100%]"
                        }
                      >
                        ₽ {(item.product.price * item.quantity).toFixed(2)}
                      </h2>
                    )}
                    {!isMobile && (
                      <span
                        className={
                          isMobile
                            ? "text-xs text-[#828282]"
                            : "text-[#828282] tracking-[-0.02em] text-[13px] font-medium leading-[19px]"
                        }
                      >
                        {item.quantity} шт
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Итоговая информация */}
        <div
          className={
            !isMobile
              ? "flex flex-col gap-2"
              : "fixed bottom-[76px] shadow-[0_2px_20px_#0000001a] rounded-t-[20px] w-[100vw] left-0 z-10"
          }
        >
          <div className={mobileStyles.orderSummary}>
            <h2
              className={`${
                isMobile
                  ? "text-[17px] font-bold mb-[10px]"
                  : "text-[#232323] text-[20px] leading-[100%] tracking-[-0.02em] mb-[15px]"
              } font-bold `}
            >
              Заказ
            </h2>
            <div
              className={
                !isMobile
                  ? "flex justify-between text-[#474A51] text-[16px] font-semibold tracking-[-0.02em] leading-[100%]"
                  : "flex justify-between text-[#474A51] font-semibold tracking-[-0.02em] leading-[100%]"
              }
            >
              <span>
                {getDeclension(basketStore.totalItems, [
                  "товар",
                  "товара",
                  "товаров",
                ])}
              </span>
              <DottedSeparator />
              <span className="text-[#474A51] font-semibold tracking-[-0.02em] leading-[100%]">
                ₽ {basketStore.totalSum.toFixed(2)}
              </span>
            </div>
            <div
              className={
                !isMobile
                  ? "mt-[15px]"
                  : "self-end text-right mb-[20px] mt-[10px]"
              }
            >
              <span
                className={
                  !isMobile
                    ? "text-[16px] text-[#383181] font-semibold tracking-[-0.02em] leading-[100%]"
                    : "text-[12px] text-[#383181] font-medium text-right self-end  ml-auto"
                }
              >
                Получение в{" "}
                {getDeclension(basketStore.uniqueWarehousesCount, [
                  "магазине",
                  "магазинах",
                  "магазинах",
                ])}
              </span>
            </div>
            <div className={!isMobile ? "mt-[30px]" : ""}>
              <Button
                className={
                  !isMobile
                    ? "w-full h-[59px] !rounded-[15px]"
                    : "w-full !rounded-[15px] h-[49px]"
                }
                style="primary"
                onClick={() => setIsOpenModal(true)}
                disabled={basketStore.basket.length === 0}
              >
                Оформить заказ
              </Button>
            </div>
          </div>
          {!isMobile && (
            <span className="text-center text-[13px] font-medium tracking-[-0.02em] leading-[19px] text-[#828282]">
              Резерв товаров осуществляется на срок 24 часа
            </span>
          )}
        </div>
      </div>

      {/* Модальное окно оформления заказа */}
      <RecipientDataModal
        isOpen={isOpenModal}
        onClose={() => {
          setIsOpenModal(false);
          setVerificationSent(false);
        }}
        onSubmit={handleRecipientSubmit}
        uniqueWarehousesCount={basketStore.uniqueWarehousesCount}
        isLoading={basketStore.isLoading}
        userAuthState={getUserAuthState()}
        userPhone={appStore.userInfo.phone || ""}
        userName={appStore.userInfo.name || ""}
        userSurname={appStore.userInfo.surname || ""}
        needsVerification={!appStore.isLoginIn}
        verificationSent={verificationSent}
        retryTimer={retryTimer}
        onRequestVerification={handleRequestVerification}
        onAuthSuccess={(data) => appStore.onAuthCompleted(data)}
      />

      {/* Модальное окно успешного оформления заказа */}
      <Modal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        closeButton={true}
        size={isMobile ? "md" : "lg"}
      >
        <div className="flex flex-col p-4">
          <h2 className={`${isMobile ? "text-xl" : "text-2xl"} font-bold mb-4`}>
            Заказ успешно оформлен!
          </h2>

          <p className="mb-4">
            {orderNumbers.length == 1
              ? "Ваш номер заказа:"
              : "Ваши номера заказов"}
          </p>

          <div className="flex flex-col gap-2 mb-6">
            {orderNumbers.map((number, index) => (
              <div key={index} className="p-3 bg-gray-100 rounded-lg">
                <span className="font-medium">{number}</span>
              </div>
            ))}
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Предьявите его менеджеру в магазине, чтобы получить заказ.
          </p>

          <Button
            style="primary"
            className="w-full"
            onClick={() => setIsSuccessModalOpen(false)}
          >
            Понятно
          </Button>
        </div>
      </Modal>

      <ToastContainer position={isMobile ? "bottom-center" : "bottom-right"} />
    </div>
  );
};

export default observer(BasketPage);
